import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requiredField, useForm } from '../../../../utils/validators';
import { Modal, Button, Form, OptionalSection, SelectLabel, ToastContext, ToggleLabel, TooltipHelp } from '@weezevent/nacre';
import CustomizeUrl from '../customize_url/customize_url';
import Widget from '../../../../models/widget';
import EventModel from '../../../../models/eventModel';
import SelectEvents from '../../../../components/events/select-events/select-events';
import Style from './modal.module.css';
import { uuid } from '../pages/new-widget';

const defaultWidgetKeys = {
    SaleTemplate: {
        widgetKey: 'tickets',
        translationKey: 'online_ticketing'
    },
    ManageTemplate: {
        widgetKey: 'customer-area',
        translationKey: 'personal_spaces'
    }
};

export default function WidgetCreationModal({ open = false, organization, onClose = () => {}, allowedTemplates = ['SaleTemplate'] }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [openOptional, setOpenOptional] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            query: {
                time_status: 'ongoing, upcoming'
            }
        },
        [organization]
    );

    const [form, setForm, setFormObject, formErrors, formValidate, setErrors] = useForm(
        {
            name: '',
            widget_class: allowedTemplates[0],
            events: [],
            widget_key: `${defaultWidgetKeys[allowedTemplates[0]].widgetKey}-${uuid()}`,
            customs: {
                auto_dimension: true,
                display_powered_by_weez: true,
                displayLanguageSelector: true,
                mainColor: '#0032fa'
            },
            all_events: false
        },
        {
            name: requiredField,
            widget_key: requiredField
        }
    );

    const templateOptions = React.useMemo(() => {
        return allowedTemplates.map(option => ({
            key: option,
            value: option,
            text: t(`ticket.${defaultWidgetKeys[option].translationKey}.list.title`)
        }));
    }, [allowedTemplates, t]);

    const isPersonalSpace = React.useMemo(() => allowedTemplates.includes('ManageTemplate'), [allowedTemplates]);

    const onChangeWidgetClass = value => {
        setFormObject(
            {
                widget_class: value,
                widget_key: `${defaultWidgetKeys[value].widgetKey}-${uuid()}`
            },
            true
        );
    };

    const onSubmit = React.useCallback(
        (redirectAfterSave = false) => {
            if (!formValidate()) {
                return;
            }
            setIsSubmitting(true);
            const _widget = new Widget({
                ...form
            });
            if (form.all_events) {
                _widget.events = []; // Clear events if all events is true
            }
            _widget
                .save({
                    organizationId: organization.id
                })
                .then(({ response }) => {
                    toast.success(t('ticket.personal_spaces.create.toast-success'));
                    if (redirectAfterSave) {
                        history.push(`/ticket/O${organization.id}/${defaultWidgetKeys[allowedTemplates[0]].translationKey}/${response.id}/settings`);
                    } else {
                        onClose();
                    }
                })
                .catch(e => {
                    if (e.code === 400) {
                        setErrors({ ...formErrors, ...e.response });
                    }
                    toast.error(t('ticket.personal_spaces.create.toast-error'));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [form, formValidate, organization, toast, t, onClose, allowedTemplates]
    );

    if (!open) {
        return null;
    }

    return (
        <Modal
            open={Boolean(open)}
            onClose={onClose}
            size="large"
            title={t('ticket.personal_spaces.create.title')}
            subtitle={t('ticket.personal_spaces.create.subtitle')}
            className={Style['personal-space-create']}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} isLoading={isSubmitting} label={t('ticket.common.action.cancel')} />
                    <Button primary inverted isLoading={isSubmitting} onClick={() => onSubmit(true)} label={t('ticket.personal_spaces.create.save-and-configure')} />
                    <Button primary isLoading={isSubmitting} onClick={() => onSubmit(false)} label={t('ticket.common.action.save')} />
                </Button.Group>
            }
        >
            <Form.Field>
                <Form.Label label={t('ticket.personal_spaces.create.name')} />
                <Form.Input
                    required
                    name="name"
                    placeholder={t('ticket.personal_spaces.create.name-placeholder')}
                    value={form.name}
                    onChange={({ target: { value } }) => {
                        setForm('name', value);
                    }}
                    max={256}
                />
                <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : ['']} />
            </Form.Field>
            <Form.Field>
                <SelectLabel disabled value={form.widget_class} label={t('ticket.personal_spaces.create.widget_class')} onChange={onChangeWidgetClass} options={templateOptions} />
            </Form.Field>
            {isPersonalSpace && (
                <Form.Field>
                    <ToggleLabel
                        inlinePosition="right"
                        checked={form.all_events}
                        onChange={e => setForm('all_events', e.target.checked)}
                        label={
                            <span>
                                {t('ticket.personal_spaces.settings.configuration.all_events')}
                                <TooltipHelp title={t('ticket.personal_spaces.settings.configuration.all_events_description')} position="right" />
                            </span>
                        }
                    />
                </Form.Field>
            )}
            <Form.Field>
                <div className={Style['selector-container']}>
                    <SelectEvents multi onChange={value => setForm('events', value)} events={events} disabled={loadingEvents || form.all_events} loading={loadingEvents} />
                </div>
            </Form.Field>
            <OptionalSection
                open={openOptional}
                onToggle={toggle => {
                    setOpenOptional(toggle);
                }}
                label={t('ticket.personal_spaces.create.widget_key')}
            >
                <CustomizeUrl baseUrl={`${window.weezConfig.api.widgetUrl}/ticket/`} form={form} setForm={setForm} formErrors={formErrors} />
            </OptionalSection>
        </Modal>
    );
}
